/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Policy, PolicyViolation, PolicyStatus, PolicyControl, EnforcementAction } from '../types/policy';
import { policyManager } from './policyManager';

export interface EnforcementRule {
  id: string;
  policyId: string;
  condition: EnforcementCondition;
  action: EnforcementActionType;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  automated: boolean;
  frequency: 'RealTime' | 'Daily' | 'Weekly' | 'Monthly';
}

export interface EnforcementCondition {
  type: 'Metric' | 'Event' | 'Time' | 'Resource' | 'Compliance';
  parameter: string;
  operator: 'equals' | 'greaterThan' | 'lessThan' | 'between' | 'contains';
  value: any;
  threshold?: number;
}

export interface EnforcementActionType {
  type: 'Warning' | 'CorrectiveAction' | 'Penalty' | 'Suspension' | 'Termination' | 'Notification' | 'AutoRemediation';
  description: string;
  cost?: number;
  consequences?: string[];
  remediationSteps?: string[];
}

export interface ComplianceScore {
  policyId: string;
  score: number;
  factors: ComplianceFactor[];
  lastUpdated: number;
  trend: number;
}

export interface ComplianceFactor {
  name: string;
  weight: number;
  value: number;
  description: string;
}

export class PolicyEnforcementEngine {
  private enforcementRules: Map<string, EnforcementRule> = new Map();
  private complianceScores: Map<string, ComplianceScore> = new Map();
  private violationHistory: Map<string, PolicyViolation[]> = new Map();
  private activeEnforcements: Map<string, EnforcementAction> = new Map();
  private monitoringMetrics: Map<string, number> = new Map();

  constructor() {
    this.initializeEnforcementRules();
    // Background intervals keep Jest alive; enforcement runs on-demand in tests.
    if (!process.env.JEST_WORKER_ID) {
      this.startMonitoring();
    }
  }

  // Core enforcement methods
  public enforcePolicies(): void {
    const policies = policyManager.getPolicies().filter(p => p.status === PolicyStatus.Enforced);

    for (const policy of policies) {
      this.evaluatePolicyCompliance(policy);
      this.checkEnforcementRules(policy);
      this.updateComplianceScore(policy);
    }
  }

  public evaluatePolicyCompliance(policy: Policy): number {
    const factors = this.calculateComplianceFactors(policy);
    const score = this.calculateOverallScore(factors);

    const complianceScore: ComplianceScore = {
      policyId: policy.id,
      score,
      factors,
      lastUpdated: Date.now(),
      trend: this.calculateTrend(policy.id, score)
    };

    this.complianceScores.set(policy.id, complianceScore);

    // Check for violations
    if (score < 80) {
      this.detectComplianceViolations(policy, score);
    }

    return score;
  }

  public addEnforcementRule(rule: EnforcementRule): void {
    this.enforcementRules.set(rule.id, rule);
  }

  public removeEnforcementRule(ruleId: string): void {
    this.enforcementRules.delete(ruleId);
  }

  public getComplianceScore(policyId: string): ComplianceScore | undefined {
    return this.complianceScores.get(policyId);
  }

  public getViolationHistory(policyId: string): PolicyViolation[] {
    return this.violationHistory.get(policyId) || [];
  }

  public getActiveEnforcements(): EnforcementAction[] {
    return Array.from(this.activeEnforcements.values());
  }

  // Compliance evaluation methods
  private calculateComplianceFactors(policy: Policy): ComplianceFactor[] {
    const factors: ComplianceFactor[] = [];

    // Implementation progress factor
    factors.push({
      name: 'Implementation Progress',
      weight: 0.25,
      value: policy.implementationProgress,
      description: 'How completely the policy has been implemented'
    });

    // Effectiveness score factor
    factors.push({
      name: 'Effectiveness Score',
      weight: 0.30,
      value: policy.effectivenessScore,
      description: 'Measurable impact of the policy'
    });

    // Violation history factor
    const violations = this.getViolationHistory(policy.id);
    const violationScore = Math.max(0, 100 - (violations.length * 10));
    factors.push({
      name: 'Violation Record',
      weight: 0.20,
      value: violationScore,
      description: 'Historical compliance record'
    });

    // Training completion factor
    const trainingScore = this.calculateTrainingCompliance(policy);
    factors.push({
      name: 'Training Compliance',
      weight: 0.15,
      value: trainingScore,
      description: 'Staff training completion rates'
    });

    // Documentation completeness factor
    const documentationScore = this.calculateDocumentationCompliance(policy);
    factors.push({
      name: 'Documentation Completeness',
      weight: 0.10,
      value: documentationScore,
      description: 'Quality and completeness of policy documentation'
    });

    return factors;
  }

  private calculateOverallScore(factors: ComplianceFactor[]): number {
    return factors.reduce((total, factor) => {
      return total + (factor.value * factor.weight);
    }, 0);
  }

  private calculateTrainingCompliance(policy: Policy): number {
    // Simplified calculation - would integrate with HR system
    const requiredTraining = policy.implementationPlan?.trainingRequirements?.length || 0;
    if (requiredTraining === 0) return 100;

    const completedTraining = Math.floor(requiredTraining * (0.6 + Math.random() * 0.4));
    return (completedTraining / requiredTraining) * 100;
  }

  private calculateDocumentationCompliance(policy: Policy): number {
    // Simplified calculation based on policy content completeness
    const content = policy.content;
    let score = 0;
    let totalChecks = 0;

    if (content.purpose) { score += 20; }
    if (content.scope) { score += 20; }
    if (content.policyStatement) { score += 20; }
    if (content.responsibilities.length > 0) { score += 20; }
    if (content.procedures.length > 0) { score += 20; }

    totalChecks = 5;
    return score;
  }

  private calculateTrend(policyId: string, currentScore: number): number {
    const previousScore = this.complianceScores.get(policyId)?.score || currentScore;
    return currentScore - previousScore;
  }

  // Violation detection and management
  private detectComplianceViolations(policy: Policy, score: number): void {
    if (score < 60) {
      // Critical violation
      this.createViolation(policy, 'Critical', 'Severe non-compliance detected');
    } else if (score < 75) {
      // High violation
      this.createViolation(policy, 'High', 'Significant compliance gaps identified');
    } else if (score < 85) {
      // Medium violation
      this.createViolation(policy, 'Medium', 'Minor compliance issues detected');
    }
  }

  private createViolation(policy: Policy, severity: any, description: string): void {
    const violation: PolicyViolation = {
      id: `violation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      policyId: policy.id,
      policyName: policy.name,
      violationType: 'NonCompliance',
      severity,
      description,
      detectedBy: 'Automated System',
      detectionDate: Date.now(),
      location: 'System-wide',
      impactedSystems: ['Policy Management System'],
      rootCauseAnalysis: 'Automated analysis required',
      immediateActions: ['Review compliance metrics', 'Identify root causes'],
      longTermRemediation: 'Pending investigation',
      responsibleParty: 'Policy Owner',
      status: 'Open',
      financialImpact: this.calculateViolationFinancialImpact(severity),
      repeatViolation: this.isRepeatViolation(policy.id)
    };

    // Add to violation history
    const history = this.violationHistory.get(policy.id) || [];
    history.push(violation);
    this.violationHistory.set(policy.id, history);

    // Report violation to policy manager
    policyManager.reportViolation(violation);

    // Trigger enforcement actions
    this.triggerEnforcementActions(policy, violation);
  }

  private calculateViolationFinancialImpact(severity: string): number {
    const impactMap = {
      'Critical': 100000,
      'High': 50000,
      'Medium': 10000,
      'Low': 2000
    };
    return impactMap[severity] || 0;
  }

  private isRepeatViolation(policyId: string): boolean {
    const history = this.violationHistory.get(policyId) || [];
    const recentViolations = history.filter(v =>
      Date.now() - v.detectionDate < (90 * 24 * 60 * 60 * 1000) // Last 90 days
    );
    return recentViolations.length > 1;
  }

  // Enforcement rule processing
  private checkEnforcementRules(policy: Policy): void {
    const relevantRules = Array.from(this.enforcementRules.values())
      .filter(rule => rule.policyId === policy.id);

    for (const rule of relevantRules) {
      if (this.evaluateEnforcementCondition(rule.condition)) {
        this.executeEnforcementAction(policy, rule);
      }
    }
  }

  private evaluateEnforcementCondition(condition: EnforcementCondition): boolean {
    const currentValue = this.getMetricValue(condition.parameter);

    switch (condition.operator) {
      case 'equals':
        return currentValue === condition.value;
      case 'greaterThan':
        return currentValue > condition.value;
      case 'lessThan':
        return currentValue < condition.value;
      case 'between':
        return currentValue >= condition.value[0] && currentValue <= condition.value[1];
      case 'contains':
        return String(currentValue).includes(condition.value);
      default:
        return false;
    }
  }

  private getMetricValue(parameter: string): number {
    // Return monitored metrics or calculate them
    return this.monitoringMetrics.get(parameter) || 0;
  }

  private executeEnforcementAction(policy: Policy, rule: EnforcementRule): void {
    const action: EnforcementAction = {
      id: `enforcement_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      violationId: '',
      actionType: rule.action.type,
      description: rule.action.description,
      severity: rule.severity,
      issuedBy: 'System',
      issuedDate: Date.now(),
      deadline: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
      status: 'Issued',
      cost: rule.action.cost || 0,
      consequences: rule.action.consequences || []
    };

    this.activeEnforcements.set(action.id, action);

    if (rule.automated) {
      this.executeAutomatedAction(action);
    }
  }

  private executeAutomatedAction(action: EnforcementAction): void {
    switch (action.actionType) {
      case 'Notification':
        this.sendEnforcementNotification(action);
        break;
      case 'AutoRemediation':
        this.performAutoRemediation(action);
        break;
      case 'Penalty':
        this.applyPenalty(action);
        break;
      default:
        console.log(`Enforcement action ${action.actionType} queued for manual execution`);
    }
  }

  private sendEnforcementNotification(action: EnforcementAction): void {
    console.log(`ENFORCEMENT NOTIFICATION: ${action.description}`);
    // Would integrate with notification system
  }

  private performAutoRemediation(action: EnforcementAction): void {
    console.log(`AUTO-REMEDIATION: Executing automated fixes for ${action.description}`);
    // Would integrate with system remediation capabilities
  }

  private applyPenalty(action: EnforcementAction): void {
    console.log(`PENALTY APPLIED: ${action.cost} - ${action.description}`);
    // Would integrate with financial system
  }

  private triggerEnforcementActions(policy: Policy, violation: PolicyViolation): void {
    // Find applicable enforcement rules
    const applicableRules = Array.from(this.enforcementRules.values())
      .filter(rule => rule.policyId === policy.id);

    for (const rule of applicableRules) {
      // Check if violation triggers this rule
      if (this.violationTriggersRule(violation, rule)) {
        this.executeEnforcementAction(policy, rule);
      }
    }
  }

  private violationTriggersRule(violation: PolicyViolation, rule: EnforcementRule): boolean {
    // Simplified logic - would be more sophisticated in production
    return violation.severity === rule.severity;
  }

  // Monitoring and analytics
  private startMonitoring(): void {
    // Start periodic compliance evaluation
    const complianceTimer = setInterval(() => {
      this.enforcePolicies();
      this.updateMonitoringMetrics();
      this.generateComplianceReports();
    }, 60000); // Every minute for demo, would be longer in production
    complianceTimer.unref?.();

    // Update metrics more frequently for real-time monitoring
    const metricsTimer = setInterval(() => {
      this.updateRealTimeMetrics();
    }, 5000);
    metricsTimer.unref?.();
  }

  private updateMonitoringMetrics(): void {
    // Update key compliance metrics
    const policies = policyManager.getPolicies().filter(p => p.status === PolicyStatus.Enforced);

    let totalCompliance = 0;
    let totalEffectiveness = 0;
    let totalViolations = 0;

    for (const policy of policies) {
      const score = this.complianceScores.get(policy.id);
      if (score) {
        totalCompliance += score.score;
      }
      totalEffectiveness += policy.effectivenessScore;
      totalViolations += this.getViolationHistory(policy.id).length;
    }

    const policyCount = policies.length || 1;

    this.monitoringMetrics.set('overall_compliance', totalCompliance / policyCount);
    this.monitoringMetrics.set('overall_effectiveness', totalEffectiveness / policyCount);
    this.monitoringMetrics.set('total_violations', totalViolations);
    this.monitoringMetrics.set('policy_count', policies.length);
  }

  private updateRealTimeMetrics(): void {
    // Update metrics that change in real-time
    this.monitoringMetrics.set('active_enforcements', this.activeEnforcements.size);
    this.monitoringMetrics.set('system_load', Math.random() * 100); // Simplified
    this.monitoringMetrics.set('response_time', 50 + Math.random() * 100); // Simplified
  }

  private generateComplianceReports(): void {
    const reports = [
      this.generateDailyReport(),
      this.generateWeeklyReport(),
      this.generateMonthlyReport()
    ];

    // Store or send reports as needed
    console.log('Compliance reports generated:', reports.length);
  }

  private generateDailyReport(): any {
    return {
      type: 'daily',
      date: new Date().toISOString(),
      overallCompliance: this.monitoringMetrics.get('overall_compliance'),
      newViolations: this.getNewViolationsCount('daily'),
      resolvedViolations: this.getResolvedViolationsCount('daily'),
      activeEnforcements: this.activeEnforcements.size
    };
  }

  private generateWeeklyReport(): any {
    return {
      type: 'weekly',
      week: this.getWeekNumber(),
      averageCompliance: this.getAverageCompliance('weekly'),
      trends: this.getComplianceTrends('weekly'),
      criticalIssues: this.getCriticalIssuesCount(),
      recommendations: this.generateRecommendations()
    };
  }

  private generateMonthlyReport(): any {
    return {
      type: 'monthly',
      month: new Date().getMonth(),
      year: new Date().getFullYear(),
      policyMaturity: this.calculatePolicyMaturity(),
      riskAssessment: this.performRiskAssessment(),
      improvementOpportunities: this.identifyImprovementOpportunities(),
      nextMonthFocus: this.getNextMonthFocus()
    };
  }

  private getNewViolationsCount(period: string): number {
    // Simplified calculation
    return Math.floor(Math.random() * 5);
  }

  private getResolvedViolationsCount(period: string): number {
    // Simplified calculation
    return Math.floor(Math.random() * 3);
  }

  private getWeekNumber(): number {
    const date = new Date();
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  private getAverageCompliance(period: string): number {
    // Simplified calculation
    return 75 + Math.random() * 20;
  }

  private getComplianceTrends(period: string): any {
    // Simplified trend data
    return {
      improving: Math.random() > 0.5,
      changePercent: (Math.random() - 0.5) * 10,
      keyDrivers: ['Training completion', 'System improvements', 'Process optimization']
    };
  }

  private getCriticalIssuesCount(): number {
    return Array.from(this.activeEnforcements.values())
      .filter(action => action.severity === 'Critical').length;
  }

  private generateRecommendations(): string[] {
    const recommendations: string[] = [];

    const overallCompliance = this.monitoringMetrics.get('overall_compliance') || 0;
    if (overallCompliance < 80) {
      recommendations.push('Increase policy training frequency');
      recommendations.push('Strengthen enforcement mechanisms');
    }

    const totalViolations = this.monitoringMetrics.get('total_violations') || 0;
    if (totalViolations > 10) {
      recommendations.push('Review high-risk policy areas');
      recommendations.push('Implement automated monitoring');
    }

    return recommendations;
  }

  private calculatePolicyMaturity(): number {
    const policies = policyManager.getPolicies();
    if (policies.length === 0) return 0;

    const maturePolicies = policies.filter(p =>
      p.status === PolicyStatus.Enforced &&
      p.effectivenessScore > 80
    ).length;

    return (maturePolicies / policies.length) * 100;
  }

  private performRiskAssessment(): any {
    return {
      overallRisk: 'Medium',
      keyRisks: [
        { type: 'Implementation', level: 'Medium' },
        { type: 'Compliance', level: 'High' },
        { type: 'Operational', level: 'Low' }
      ],
      mitigationActions: [
        'Enhanced monitoring',
        'Additional training',
        'Process improvements'
      ]
    };
  }

  private identifyImprovementOpportunities(): string[] {
    return [
      'Automate policy compliance monitoring',
      'Implement predictive compliance analytics',
      'Enhance employee training programs',
      'Strengthen cross-functional collaboration'
    ];
  }

  private getNextMonthFocus(): string[] {
    return [
      'High-risk policy areas',
      'Recent violation patterns',
      'Upcoming regulatory changes',
      'Technology updates'
    ];
  }

  private initializeEnforcementRules(): void {
    // Default enforcement rules for common compliance scenarios

    // Low compliance rule
    const lowComplianceRule: EnforcementRule = {
      id: 'low_compliance_rule',
      policyId: '', // Will be set for each policy
      condition: {
        type: 'Metric',
        parameter: 'compliance_score',
        operator: 'lessThan',
        value: 75
      },
      action: {
        type: 'Warning',
        description: 'Policy compliance below acceptable threshold',
        consequences: ['Increased monitoring', 'Management notification']
      },
      severity: 'Medium',
      automated: true,
      frequency: 'Daily'
    };

    // Critical violation rule
    const criticalViolationRule: EnforcementRule = {
      id: 'critical_violation_rule',
      policyId: '',
      condition: {
        type: 'Event',
        parameter: 'critical_violation',
        operator: 'equals',
        value: true
      },
      action: {
        type: 'CorrectiveAction',
        description: 'Immediate corrective action required for critical violation',
        cost: 10000,
        consequences: ['Emergency response team activation', 'Regulatory notification'],
        remediationSteps: ['Root cause analysis', 'Immediate remediation', 'Preventive measures']
      },
      severity: 'Critical',
      automated: false,
      frequency: 'RealTime'
    };

    this.enforcementRules.set(lowComplianceRule.id, lowComplianceRule);
    this.enforcementRules.set(criticalViolationRule.id, criticalViolationRule);
  }

  // Public API for external integration
  public getMonitoringDashboard(): any {
    return {
      overallCompliance: this.monitoringMetrics.get('overall_compliance'),
      overallEffectiveness: this.monitoringMetrics.get('overall_effectiveness'),
      totalViolations: this.monitoringMetrics.get('total_violations'),
      activeEnforcements: this.monitoringMetrics.get('active_enforcements'),
      systemLoad: this.monitoringMetrics.get('system_load'),
      responseTime: this.monitoringMetrics.get('response_time'),
      policyCount: this.monitoringMetrics.get('policy_count'),
      lastUpdated: Date.now()
    };
  }

  public getComplianceTrend(policyId: string, days: number = 30): any {
    // Generate historical trend data
    const trend = [];
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now - (i * dayMs));
      const score = 70 + Math.random() * 25; // Simplified historical data

      trend.push({
        date: date.toISOString().split('T')[0],
        score: score,
        violations: Math.floor(Math.random() * 3)
      });
    }

    return trend;
  }

  public triggerComplianceAudit(policyId?: string): any {
    const policiesToAudit = policyId
      ? [policyManager.getPolicy(policyId)].filter(Boolean) as Policy[]
      : policyManager.getPolicies().filter(p => p.status === PolicyStatus.Enforced);

    const auditResults = [];

    for (const policy of policiesToAudit) {
      const complianceScore = this.evaluatePolicyCompliance(policy);
      const violations = this.getViolationHistory(policy.id);

      auditResults.push({
        policyId: policy.id,
        policyName: policy.name,
        complianceScore,
        violationsCount: violations.length,
        riskLevel: this.calculateRiskLevel(complianceScore),
        recommendations: this.generatePolicyRecommendations(policy)
      });
    }

    return {
      auditId: `audit_${Date.now()}`,
      date: new Date().toISOString(),
      auditedPolicies: policiesToAudit.length,
      results: auditResults,
      summary: this.generateAuditSummary(auditResults)
    };
  }

  private calculateRiskLevel(score: number): string {
    if (score >= 90) return 'Low';
    if (score >= 75) return 'Medium';
    if (score >= 60) return 'High';
    return 'Critical';
  }

  private generatePolicyRecommendations(policy: Policy): string[] {
    const recommendations: string[] = [];
    const score = this.complianceScores.get(policy.id)?.score || 0;

    if (score < 80) {
      recommendations.push('Increase training and awareness programs');
      recommendations.push('Strengthen monitoring and reporting');
    }

    if (policy.implementationProgress < 100) {
      recommendations.push('Complete policy implementation');
    }

    if (policy.effectivenessScore < 75) {
      recommendations.push('Review and optimize policy controls');
    }

    return recommendations;
  }

  private generateAuditSummary(results: any[]): any {
    const totalPolicies = results.length;
    const averageScore = results.reduce((sum, r) => sum + r.complianceScore, 0) / totalPolicies;
    const totalViolations = results.reduce((sum, r) => sum + r.violationsCount, 0);
    const criticalIssues = results.filter(r => this.calculateRiskLevel(r.complianceScore) === 'Critical').length;

    return {
      averageCompliance: averageScore,
      totalViolations,
      criticalIssues,
      overallHealth: this.calculateRiskLevel(averageScore),
      keyFindings: [
        `${totalPolicies} policies audited`,
        `${totalViolations} total violations found`,
        `${criticalIssues} policies require immediate attention`
      ]
    };
  }
}

// Export singleton instance
export const policyEnforcementEngine = new PolicyEnforcementEngine();